import { ErrorBoundary } from "@datadog/browser-rum-react";
import AlertTriangleIcon from "@redotech/redo-icons/alert-triangle.svg";
import { RedoIcon } from "@redotech/redo-web/arbiter-components/icon/redo-icon";
import { type ReactNode, memo } from "react";

import * as css from "./page-error-boundary.module.css";

/**
 * Per-page error boundary (decision 4A). Wraps each route's `<Outlet/>`
 * content so a render bug in the dashboard, settings, etc. shows an
 * inline fallback INSIDE the main content area while the sidebar +
 * topbar stay visible — the user can still click logout, navigate
 * away, retry. The root-level `<ErrorBoundary>` in `index.tsx` stays
 * for catastrophic shell failures.
 *
 * The fallback never renders the underlying error message — server
 * traces and stack frames don't belong in user-facing UI.
 */
export const PageErrorBoundary = memo(function PageErrorBoundary({
  children,
}: {
  children: ReactNode;
}) {
  return <ErrorBoundary fallback={PageErrorFallback}>{children}</ErrorBoundary>;
});

function PageErrorFallback({ resetError }: { resetError: () => void }) {
  return (
    <div className={css.fallback} role="alert">
      <span aria-hidden className={css.fallbackIcon}>
        <RedoIcon Icon={AlertTriangleIcon} size={20} />
      </span>
      <h1 className={css.fallbackTitle}>Something went wrong on this page</h1>
      <p className={css.fallbackMessage}>
        Try reloading the page. If this keeps happening, let us know.
      </p>
      <button className={css.fallbackButton} onClick={resetError} type="button">
        Try again
      </button>
    </div>
  );
}
